/**
 * Datentypen der Beteiligten-Erfassung am Gebäude.
 *
 * Grundsatz (Plan §0, E8): Personen und Firmen sind überall **Kontakte**, kein
 * Freitext. Wie der Kontakt entsteht, hängt am Weg:
 *
 *  - **intern (Backoffice, authentifiziert):** echte Kontaktauswahl über
 *    `ContactPickerField` — inklusive Neuanlage im selben Dialog. `contactId`
 *    ist dann gesetzt.
 *  - **öffentlicher Kundenweg (Token, unauthentifiziert):** der Kunde bekommt
 *    ausdrücklich **keinen** Zugriff auf den Kontaktbestand. Er gibt die Daten
 *    strukturiert ein (`contactId: null`), das Back-Office macht beim
 *    Übernehmen einen Kontakt daraus und stempelt `uebernommenAlsContactId` /
 *    `uebernommenAt`. Genau dieses Muster fährt `VerantEntry` in `forms-core`
 *    bereits (`risikoanalyseStep/VerantwortlichkeitenList.tsx`).
 *
 * `KontaktRef` ist ein Superset von `ContactPickerValue` (`forms-core`) und
 * damit direkt an `ContactPickerField` übergebbar.
 */

export type KontaktRef = {
  /** Gesetzt, sobald ein echter KMVS-Kontakt dahinter steht. */
  contactId: number | null;
  /** Klartext-Name — Snapshot bzw. Eingabe des Kunden. */
  label: string;
  email?: string;
  telefon?: string;
  /** Vom Back-Office beim Übernehmen gesetzt (Muster `VerantEntry`). */
  uebernommenAlsContactId?: string;
  uebernommenAt?: string;
};

/** Ein Eintrag einer 1:n-Rolle (Mieter, Pächter, sonstige Nutzer, …). */
export type BeteiligtenEintrag = {
  id: string;
  kontaktRef?: KontaktRef;
  /** Gespiegelter Klartext-Name — auch ohne Kontaktbezug auswertbar. */
  name: string;
  /** Betroffene Einheit / Fläche. */
  einheit?: string;
  /** Beginn (ISO-Datum, `type="date"`). */
  von?: string;
  /** Ende (ISO-Datum); leer = unbefristet. */
  bis?: string;
  /** Art der Nutzung / Verbindung. */
  art?: string;
  /** Anteil in Prozent (Miteigentum). */
  anteilProzent?: string;
  bemerkung?: string;
};

/** Welche Zusatzfelder ein Listentyp zeigt. */
export type BeteiligtenFeld =
  | "einheit"
  | "von"
  | "bis"
  | "art"
  | "anteilProzent"
  | "bemerkung";

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Liest einen `KontaktRef` aus einem Answer-Wert. Deckt drei Fälle ab:
 * bereits geparstes Objekt, Alt-Bestand als reiner Freitext-String und
 * „nichts gesetzt". Denselben Guard fährt `QuestionField` für den
 * contactPicker — nach dem Parse-Seam ist der Wert zwar meist ein Objekt, bei
 * Alt-Daten aber weiterhin ein String.
 */
export const asKontaktRef = (value: unknown): KontaktRef | undefined => {
  if (isObject(value) && "contactId" in value) {
    const v = value as Record<string, unknown>;
    return {
      contactId: typeof v.contactId === "number" ? v.contactId : null,
      label: String(v.label ?? ""),
      email: typeof v.email === "string" ? v.email : undefined,
      telefon: typeof v.telefon === "string" ? v.telefon : undefined,
      uebernommenAlsContactId:
        typeof v.uebernommenAlsContactId === "string"
          ? v.uebernommenAlsContactId
          : undefined,
      uebernommenAt:
        typeof v.uebernommenAt === "string" ? v.uebernommenAt : undefined,
    };
  }
  if (typeof value === "string" && value.trim() !== "") {
    return { contactId: null, label: value };
  }
  return undefined;
};

/** Liste aus einem Answer-Wert lesen; alles Unbrauchbare fällt heraus. */
export const asBeteiligtenListe = (value: unknown): BeteiligtenEintrag[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(isObject).map((e, idx) => ({
    id: typeof e.id === "string" && e.id ? e.id : `b-${idx}`,
    kontaktRef: asKontaktRef(e.kontaktRef),
    name: String(e.name ?? ""),
    einheit: typeof e.einheit === "string" ? e.einheit : undefined,
    von: typeof e.von === "string" ? e.von : undefined,
    bis: typeof e.bis === "string" ? e.bis : undefined,
    art: typeof e.art === "string" ? e.art : undefined,
    anteilProzent:
      typeof e.anteilProzent === "string" ? e.anteilProzent : undefined,
    bemerkung: typeof e.bemerkung === "string" ? e.bemerkung : undefined,
  }));
};

export const newBeteiligtenId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `b-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
